
import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { logOutFromFacebook } from '@/utils/helpers';
import { formatPhoneNumberForDisplay } from '@/utils/format';
import AvatarIcon from '../../assets/images/icons/user-avatar.svg?inline';
import VisaLogoIcon from '../../assets/images/icons/visa-logo-icon-blue.svg?inline';
import MastercardLogoIcon from '../../assets/images/icons/mastercard-logo-icon.svg?inline';
import AmexLogoIcon from '../../assets/images/icons/amex-logo-icon.svg?inline';
import CreditsInfo from '@/components/shared/CreditsInfo.vue';
import TextInputButton from '@/components/shared/TextInputButton.vue';

const namespace: string = 'auth';

@Component<ProfileSettings>({
	components: {
		AvatarIcon,
		VisaLogoIcon,
		MastercardLogoIcon,
		AmexLogoIcon,
		CreditsInfo,
		TextInputButton
	}
})
export default class ProfileSettings extends Vue {
	@Action('logout', { namespace }) private logout!: () => Promise<void>;
	@Action('suiteLogout', { namespace }) private suiteLogout!: () => Promise<void>;
	@Action('addSubsidyCode', { namespace }) private addSubsidyCode!: (payload: { token: string; userId: string; fullName: string; email: string; code: string; }) => Promise<void>;
	@Action('deleteSubsidyCode', { namespace }) private deleteSubsidyCode!: (payload: { token: string; userId: string; code: string; }) => Promise<void>;
	@Action('addUserVoucher', { namespace }) private addUserVoucher!: (payload: AddUserVoucherPayload) => Promise<void>;
	@Action('deleteUserVoucher', { namespace }) private deleteUserVoucher!: (payload: DeleteUserVoucherPayload) => Promise<void>;
	@Getter('getUser', { namespace }) private user!: UserInfo;
	@Getter('getUserCredits', { namespace }) private credits!: CreditsUserInfo;
	@Getter('showLoginAsBanner', { namespace }) private showLoginAsBanner!: boolean;
	@Getter('getUserVoucher', { namespace }) private savedVoucher!: UserVoucher | null;
	@Getter('getRestaurant', { namespace: 'restaurant' }) private restaurant!: Restaurant;
	@Getter('hasSubsidies', { namespace: 'restaurant' }) private hasSubsidies!: boolean;
	@Getter('getSupportEmail', { namespace: 'restaurant' }) private supportEmail!: string;
	@Getter('getRestaurantVoucher', { namespace: 'restaurant' }) private restaurantVoucher!: object | undefined;
	@Getter('isEventDayOrdering', { namespace: 'suites' }) private isEventDayOrdering!: boolean;
	private promoCode: string = '';
	private subsidyInput: string = '';
	private voucherInput: string = '';
	private loading = {
		promoCode: false,
		subsidyCode: false,
		voucher: false,
		logout: false
	};

	/**
	 * Full name of the user
	 *
	 * @return {string}
	 */
	private get fullName(): string {
		return (this.user.firstName || this.user.lastName) ? `${this.user.firstName} ${this.user.lastName}` : '';
	}

	/**
	 * Payment method selected as default
	 *
	 * @return {PaymentOption | undefined}
	 */
	private get selectedPaymentMethod(): SavedPaymentOption | undefined {
		return this.user.paymentMethods.find(paymentOption => paymentOption.isDefault);
	}

	private get supportLink(): string {
		return this.supportEmail
			? `mailto:${this.supportEmail}?subject=${this.$t('menu.footer_support.email_subject', { restaurantName: this.restaurant.name })}`
			: 'https://app8.freshdesk.com/support/tickets/new';
	}

	/**
	 * Format phone number for display in international format
	 *
	 * @param {string} phoneNumber
	 * @return {string}
	 */
	private formatPhoneNumber(phoneNumber: string): string {
		return formatPhoneNumberForDisplay(phoneNumber);
	}

	/**
	 * Redirect to the page provided
	 *
	 * @param {string} path
	 * @return {void}
	 */
	private redirectTo(path: string): void {
		this.$router.push({ path: path, query: this.$route.query, hash: this.$route.hash }).catch(() => {});
	}

	/**
	 * Log user out
	 * Clear cookies and state
	 * Redirect to login page
	 *
	 * @return {Promise<void>}
	 */
	private async logoutUser(): Promise<void> {
		try {
			this.loading.logout = true;
			logOutFromFacebook();
			await this.logout();

			this.redirectTo('/login');
		}
		catch (error) {
			this.$toasted.show(this.$t('profile.auth.login.form.error_general'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading.logout = false;
		}
	}

	/**
	 * Log out suite operator from the suite owner's account
	 *
	 * @return {Promise<void>}
	 */
	private async logoutFromSuite(): Promise<void> {
		try {
			this.loading.logout = true;
			await this.suiteLogout();

			this.$router.push({ path: `/${this.restaurant.slug}/events`, query: { suiteOperator: 'true' }, hash: this.$route.hash }).catch(() => {});
		}
		catch (error) {
			this.$toasted.show(this.$t('profile.auth.login.form.error_general'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading.logout = false;
		}
	}

	/**
	 * Apply subsidy code
	 *
	 * @return {Promise<void>}
	 */
	private async saveSubsidyCode(): Promise<void> {
		const code = this.subsidyInput;
		if (!code) {
			return;
		}

		try {
			this.loading.subsidyCode = true;
			await this.addSubsidyCode({ token: this.user.token, userId: this.user.id, fullName: `${this.user.firstName} ${this.user.lastName}`.trim(), email: this.user.email, code });
			this.$toasted.show(this.$t('profile.index.subsidy_code_applied', { code }), { type: 'success', position: 'top-center' }).goAway(3000);
		}
		catch (error) {
			this.$toasted.show(this.$t('profile.index.subsidy_code_apply_failed'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading.subsidyCode = false;
		}
	}

	/**
	 * Remove subsidy code (WILL BE MODIFIED LATER ONCE WE IMPLEMENT THE BUTTON)
	 *
	 * @return {Promise<void>}
	 */
	private async removeSubsidyCode(): Promise<void> {
		const code = this.user.subsidyCode;
		if (!code) {
			return;
		}

		try {
			this.loading.subsidyCode = true;
			await this.deleteSubsidyCode({ token: this.user.token, userId: this.user.id, code });
			this.$toasted.show(this.$t('profile.index.subsidy_code_deleted', { code }), { type: 'success', position: 'top-center' }).goAway(3000);
		}
		catch (error) {
			this.$toasted.show(this.$t('profile.index.subsidy_code_delete_failed'), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			this.loading.subsidyCode = false;
		}
	}

	/**
	 * Save gift card / voucher to profile
	 *
	 * @return {Promise<void>}
	 */
	private async saveVoucher(): Promise<void> {
		if (!this.voucherInput) {
			return;
		}

		try {
			this.loading.voucher = true;
			await this.addUserVoucher({
				locationId: this.restaurant.app8_restaurant,
				giftCardNumber: this.voucherInput,
				userId: this.user.id,
				token: this.user.token,
				overwriteExistingCard: !!this.savedVoucher
			});
			this.voucherInput = '';
			this.$toasted.show(this.$t('profile.index.gift_card_saved'), { type: 'success', position: 'top-center' }).goAway(3000);
		}
		catch (errorMessage) {
			this.$toasted.show(this.$t('profile.index.error_saving_gift_card', { errorMessage }), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			setTimeout(() => {
				this.loading.voucher = false;
			}, 500);
		}
	}

	/**
	 * Delete gift card / voucher from profile
	 *
	 * @return {Promise<void>}
	 */
	private async deleteVoucher(): Promise<void> {
		if (!this.savedVoucher?.id) {
			return;
		}

		try {
			this.loading.voucher = true;
			await this.deleteUserVoucher({
				locationId: this.restaurant.app8_restaurant,
				giftCardId: this.savedVoucher.id,
				userId: this.user.id,
				token: this.user.token
			});
			this.$toasted.show(this.$t('profile.index.gift_card_deleted'), { type: 'success', position: 'top-center' }).goAway(3000);
		}
		catch (errorMessage) {
			this.$toasted.show(this.$t('profile.index.error_deleting_gift_card', { errorMessage }), { type: 'error', position: 'top-center' }).goAway(5000);
		}
		finally {
			setTimeout(() => {
				this.loading.voucher = false;
			}, 500);
		}
	}
}
